/* eslint-disable react-refresh/only-export-components -- readTheme and applyTheme are
   the non-React half of this component: they mirror what public/theme-init.js does
   before first paint, and live beside it so the two stay in step. */
import { useEffect, useState, type ReactNode } from "react";
import { MOBILE_MEDIA_QUERY, THEME_STORAGE_KEY } from "../constants";
import { theme as copy } from "../content";

export type Theme = "auto" | "light" | "dark";

// On phones the app just follows the system theme, so there's no picker and no stored override.
const NEXT: Record<Theme, Theme> = { auto: "light", light: "dark", dark: "auto" };

export function readTheme(): Theme {
  try {
    const t = localStorage.getItem(THEME_STORAGE_KEY);
    if (t === "light" || t === "dark") return t;
  } catch {
    // storage blocked; fall back to auto
  }
  return "auto";
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === "auto") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
}

const ICONS: Record<Theme, ReactNode> = {
  // half-filled circle: follows the system
  auto: (
    <>
      <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M12 4a8 8 0 0 1 0 16z" fill="currentColor" />
    </>
  ),
  light: (
    <>
      <circle cx="12" cy="12" r="4" fill="currentColor" />
      <path
        d="M12 2.5v2.5M12 19v2.5M2.5 12H5M19 12h2.5M5.3 5.3l1.8 1.8M16.9 16.9l1.8 1.8M5.3 18.7l1.8-1.8M16.9 7.1l1.8-1.8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </>
  ),
  dark: <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" fill="currentColor" />,
};

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() => window.matchMedia(MOBILE_MEDIA_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MEDIA_QUERY);
    const onChange = () => setMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return mobile;
}

/** Cycles Auto (follow system) → Light → Dark, and remembers the choice in this browser. */
export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(readTheme);
  const mobile = useIsMobile();

  useEffect(() => {
    applyTheme(mobile ? "auto" : theme);
    if (mobile) return;
    try {
      if (theme === "auto") localStorage.removeItem(THEME_STORAGE_KEY);
      else localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // not persisted; still applies for this session
    }
  }, [theme, mobile]);

  if (mobile) return null;

  const current = copy.labels[theme];
  const next = copy.labels[NEXT[theme]];
  return (
    <button
      type="button"
      className="theme-btn"
      onClick={() => setTheme(NEXT[theme])}
      title={copy.title(current, next)}
      aria-label={copy.ariaLabel(current, next)}
    >
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
        {ICONS[theme]}
      </svg>
    </button>
  );
}
